import View from "./view.js";
import { orderTypeLabel } from "../orderTypeLabel.js";
import { formatInvoiceNumber, invoiceLabel } from "../invoiceNumber.js";
import { esc } from "../escape.js";
import { formatMoney, currencyForCountry } from "../currency.js";
import { extractInclusiveTax, taxLabel, splitTaxableGross, exemptLabel } from "../inclusiveTax.js";
import { redeemedRewardLineIds } from "../loyalty.js";
import { normalizePaper, normalizePosition, getPosition } from "../printSettings.js";
import { qrSvg } from "../receiptQr.js";

class ReceiptView extends View {
  // Builds the receipt as separable pieces so it can print either as its own
  // document (popup / iframe) or inside the app page itself (iOS - see
  // _printInPage). `css(root)` scopes every rule under `root` ('body' for the
  // standalone document). `width` is 58 | 80 | 'full' (normal sheet printer:
  // an 80mm-wide receipt centered on whatever page size the printer uses).
  // `position` ('default' | 'center' | 'left', see printSettings.js) defaults
  // to this device's saved choice; Test print passes the staged one.
  _receiptParts(sale, { width = 80, position = getPosition() } = {}) {
    const paper = normalizePaper(width);
    const isFull = paper === "full";
    const mm = isFull ? 80 : paper;
    // The printer's actual print pipeline (Windows driver, not our own Web
    // Bluetooth path) renders on a physical page wider than the 58mm we
    // request, leaving the flush-left receipt off-center - live-measured by
    // Kelly, tuned 0.5in -> 0.25in -> 0.125in -> 0. 80mm wasn't reported as
    // needing this.
    const marginLeft = mm === 58 ? "0" : "0";
    // At 80mm the printable area is narrower than the requested page - the
    // right edge of every price line was getting sliced off. Live-measured
    // at ~1/8in cut off, then tuned another 1/8in further (0.25in total).
    const contentWidth = isFull ? "80mm" : mm === 80 ? "calc(80mm - 0.25in)" : `${mm}mm`;
    // Bottom of the receipt gets cut off on real hardware. A -1in top margin
    // was tried to reclaim space by pulling content up, but it overshot and
    // clipped the HEADER (store name/address) off the top instead - reverted.
    // Do not reposition vertically again without a precise measurement from
    // Kelly; the real fix is likely shortening total content height, not
    // shifting position (see pointbunny-app-todos.md for the same lesson
    // learned on horizontal centering).
    const marginTop = "0";
    // Money on the receipt follows the sale's country currency (falls back to the
    // active business currency for older sales without a stored country).
    const m = (n) => formatMoney(n, sale.addressCountry ? { code: currencyForCountry(sale.addressCountry) } : {});
    const date = new Date(sale.date);
    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    // Free-item reward redemptions show as $0 on the item's own row instead
    // (see itemsHtml below) - excluded here so the discount isn't listed twice.
    const redeemedLineIds = redeemedRewardLineIds(sale.adjustments);
    const activeAdj = (sale.adjustments ?? []).filter(
      (a) => !a.removed && a.computedAmount !== undefined && a.source !== "loyalty_reward",
    );
    const removedAdj = sale.showRemovedAdjustments
      ? (sale.removedAdjustments ?? [])
      : [];

    const storeName = esc(
      sale.storeName?.includes("@") ? "POINTBUNNY POS" : (sale.storeName ?? "POINTBUNNY POS")
    );

    const paymentMethod = sale.paymentMethod ?? "cash";
    const paymentLabel = { cash: "Cash", card: "Card", qr: "QR" }[paymentMethod] ?? "Cash";

    // Tax-inclusive breakdown: when the prices already include tax, show the
    // extracted VAT/GST portion (total is unchanged). Nothing when not inclusive.
    // Per-item exemption (2026-07-07): tax is extracted only from the taxable
    // portion; exempt items get their own gross line instead. Gated on the sale
    // actually being tax-inclusive (not on taxableGross > 0) so a sale that's
    // ENTIRELY exempt items still shows its exempt-sales line, just no VAT rows.
    const isInclusiveSale = Number(sale.taxInclusiveRate) > 0;
    const { taxableGross, exemptGross } = splitTaxableGross(sale.items, (it) => it.taxClass === "exempt");
    const vat = isInclusiveSale ? extractInclusiveTax(taxableGross, sale.taxInclusiveRate) : null;
    const vatLbl = esc(taxLabel(sale.addressCountry));
    const vatRows = vat
      ? `
    <tr>
      <td style="font-size:11px;padding:2px 4px 1px 0;">${vatLbl}able Sales</td>
      <td style="font-size:11px;text-align:right;">${m(vat.net)}</td>
    </tr>
    <tr>
      <td style="font-size:11px;padding:1px 4px 2px 0;">${vatLbl} (${vat.rate}%)</td>
      <td style="font-size:11px;text-align:right;">${m(vat.tax)}</td>
    </tr>`
      : "";
    // Tax added at checkout: a sale with a tax line also lists its exempt sales.
    const hasAddedTax = (sale.adjustments ?? []).some((a) => a.isTax && !a.removed);
    const exemptRow = (isInclusiveSale || hasAddedTax) && exemptGross > 0
      ? `
    <tr>
      <td style="font-size:11px;padding:1px 4px 2px 0;">${exemptLabel(sale.addressCountry)} Sales</td>
      <td style="font-size:11px;text-align:right;">${m(exemptGross)}</td>
    </tr>`
      : "";

    const itemsHtml = sale.items
      .map((item) => {
        const variants = esc(item.selectedVariants?.map((v) => v.variantName).join(", ") ?? "");
        const isFreeReward = redeemedLineIds.has(item.lineId);
        const priceHtml = isFreeReward
          ? `<span style="text-decoration:line-through;color:#888;">${m(item.totalPrice)}</span> ${m(0)}`
          : m(item.totalPrice);
        return `
        <tr>
          <td style="padding:2px 4px 2px 0;vertical-align:top;">${esc(item.itemName)} x${item.quantity}</td>
          <td style="padding:2px 0;text-align:right;vertical-align:top;white-space:nowrap;">${priceHtml}</td>
        </tr>
        ${variants ? `<tr><td colspan="2" style="font-size:10px;color:#555;padding:0 0 4px 0;">${variants}</td></tr>` : ""}
      `;
      })
      .join("");

    const hasAdj = activeAdj.length > 0 || removedAdj.length > 0;
    const adjSection = hasAdj
      ? `
      <hr class="divider">
      <table>
        <tr>
          <td style="padding:2px 4px 2px 0;font-size:11px;">Subtotal</td>
          <td style="text-align:right;font-size:11px;">${m(sale.subtotal ?? sale.totalPrice)}</td>
        </tr>
        ${activeAdj
          .map(
            (adj) => `
          <tr>
            <td style="padding:1px 4px 1px 0;font-size:11px;">${esc(adj.name)}${adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : ""}</td>
            <td style="text-align:right;font-size:11px;">${adj.computedAmount >= 0 ? "+" : "-"}${m(Math.abs(adj.computedAmount))}</td>
          </tr>
        `,
          )
          .join("")}
        ${removedAdj
          .map(
            (adj) => `
          <tr style="text-decoration:line-through;opacity:0.5;">
            <td style="padding:1px 4px 1px 0;font-size:11px;">${esc(adj.name)}${adj.calculation === "percentage" ? ` (${adj.appliedValue}%)` : ""} (removed)</td>
            <td style="text-align:right;font-size:11px;">--</td>
          </tr>
        `,
          )
          .join("")}
      </table>
    `
      : "";

    // Full page always centers the receipt column. Thermal widths follow the
    // owner's Receipt position pick - "default" is the live-tuned flush-left
    // placement above. Auto-centering is a choice, not the default, because it
    // overshot on Kelly's printer (see pointbunny-app-todos.md); "left" pulls
    // the column 3mm into its own 4mm side padding, so no text is clipped.
    const pos = normalizePosition(position);
    const hMargin = isFull || pos === "center"
      ? "margin-left:auto; margin-right:auto;"
      : pos === "left"
        ? "margin-left:-3mm;"
        : `margin-left:${marginLeft};`;
    const css = (root) => {
      const p = root === "body" ? "" : `${root} `;
      const all = root === "body" ? "*" : `${root}, ${root} *`;
      return `
  ${all} { margin:0; padding:0; box-sizing:border-box; }
  ${root} { font-family:'Courier New',Courier,monospace; font-size:12px; width:${contentWidth}; ${hMargin} margin-top:${marginTop}; padding:6mm 4mm; color:#000; }
  ${p}table { width:100%; border-collapse:collapse; }
  ${p}.center { text-align:center; }
  ${p}.divider { border:none; border-top:1px dashed #000; margin:6px 0; }
  ${p}.brand { font-size:15px; font-weight:bold; letter-spacing:1px; }
  ${p}.free-plan-badge { margin:10px auto 0; max-width:46mm; padding:7px 8px 6px; border:1px solid #000; border-radius:8px; color:#000; }
  ${p}.free-plan-name { font-size:11px; font-weight:bold; letter-spacing:2px; }
  ${p}.free-plan-slogan { margin-top:3px; font-size:10px; line-height:1.35; }
  ${p}.free-plan-cta { margin-top:4px; font-size:10px; font-weight:bold; }`;
    };
    const page = isFull ? "@page { margin:12mm; }" : `@page { size:${mm}mm auto; margin:0; }`;

    // Custom footer + feedback QR are Pro-only (the free plan keeps the default
    // thank-you line and the Pointbunny badge).
    const feedbackQr = sale.isFreePlan ? "" : qrSvg(sale.receiptQrUrl);
    // Tip sits outside the sale total (never taxed); printed under TOTAL.
    const tip = Number(sale.tipAmount) || 0;

    const body = `
  <div class="center brand">${storeName}</div>
  ${sale.addressStreet ? `
  <div class="center" style="font-size:9px;color:#555;margin:2px 0 0;line-height:1.35;">
    <div>${esc(sale.addressStreet)}</div>
    ${(() => {
      // Country is intentionally omitted: the street/city/province/zip already
      // identify the business and the customer is local (the thermal/ESC-POS
      // receipt omits it too). addressCountry is still used for localized labels.
      const locality = [sale.addressCity, sale.addressProvince].filter(Boolean).join(', ');
      const localityZip = [locality, sale.addressZip].filter(Boolean).join(' ');
      return localityZip ? `<div>${esc(localityZip)}</div>` : '';
    })()}
  </div>` : ''}
  ${(sale.businessPhone && sale.showBusinessPhone !== false) ? `<div class="center" style="font-size:9px;color:#555;margin:1px 0 0;">${esc(sale.businessPhone)}</div>` : ''}
  <div class="center" style="margin:4px 0 2px;font-size:11px;">${dateStr} &nbsp; ${timeStr}</div>
  ${(sale.showInvoiceNumber !== false && sale.receiptNumber != null) ? `<div class="center" style="font-size:11px;font-weight:bold;letter-spacing:0.04em;margin:0 0 2px;">${invoiceLabel(sale.addressCountry)} ${esc(formatInvoiceNumber(sale.receiptNumber, sale.date))}</div>` : ''}
  ${(sale.cashierName && sale.showCashier !== false) ? `<div class="center" style="font-size:10px;color:#555;margin:0 0 2px;">Cashier: ${esc(sale.cashierName)}</div>` : ''}
  ${sale.orderType ? `<div class="center" style="font-size:11px;font-weight:bold;letter-spacing:0.05em;margin:0 0 4px;">${orderTypeLabel(sale.orderType, sale.addressCountry, { upper: true })}</div>` : ''}
  ${sale.tableName ? `<div class="center" style="font-size:13px;font-weight:bold;margin:0 0 4px;">TABLE ${esc(sale.tableName)}</div>` : ''}
  ${(sale.showTicketNumber !== false && sale.ticketNumber != null) ? `
  <div class="center" style="margin:6px 0;padding:6px 0;border-top:2px dashed #000;border-bottom:2px dashed #000;">
    <div style="font-size:11px;letter-spacing:0.08em;">TICKET</div>
    <div style="font-size:32px;font-weight:bold;line-height:1.1;">#${sale.ticketNumber}</div>
  </div>` : ''}
  <hr class="divider">

  <table>${itemsHtml}</table>

  ${adjSection}

  <hr class="divider">
  <table>
    <tr>
      <td style="font-weight:bold;font-size:13px;padding:3px 4px 3px 0;">TOTAL</td>
      <td style="font-weight:bold;font-size:13px;text-align:right;">${m(sale.totalPrice)}</td>
    </tr>
    ${vatRows}
    ${exemptRow}
    ${tip > 0 ? `
    <tr>
      <td style="font-size:11px;padding:3px 4px 1px 0;">Tip</td>
      <td style="font-size:11px;text-align:right;">${m(tip)}</td>
    </tr>
    <tr>
      <td style="font-weight:bold;font-size:12px;padding:1px 4px 3px 0;">TOTAL WITH TIP</td>
      <td style="font-weight:bold;font-size:12px;text-align:right;">${m(Number(sale.totalPrice) + tip)}</td>
    </tr>` : ''}
    <tr>
      <td style="font-size:11px;padding:3px 4px 2px 0;">Payment</td>
      <td style="font-size:11px;text-align:right;">${paymentLabel}</td>
    </tr>
    ${paymentMethod === "cash" ? `
    <tr>
      <td style="font-size:11px;padding:1px 4px 2px 0;">Cash Tendered</td>
      <td style="font-size:11px;text-align:right;">${m(sale.customerPayment)}</td>
    </tr>
    <tr>
      <td style="font-size:11px;font-weight:bold;padding:2px 4px 2px 0;">Change</td>
      <td style="font-size:11px;font-weight:bold;text-align:right;">${m(sale.customerChange)}</td>
    </tr>` : `
    <tr>
      <td style="font-size:11px;font-weight:bold;padding:2px 4px 2px 0;">Paid in full</td>
      <td style="font-size:11px;font-weight:bold;text-align:right;">${m(Number(sale.totalPrice) + tip)}</td>
    </tr>`}
  </table>
  <hr class="divider">
  <div class="center" style="font-size:10px;color:#555;margin-top:4px;">
    <p>${(!sale.isFreePlan && sale.receiptFooter) ? esc(sale.receiptFooter) : 'Thank you for your purchase!'}</p>
    ${feedbackQr ? `<div style="margin:6px auto 2px;width:28mm;">${feedbackQr}</div>` : ''}
    ${sale.isFreePlan ? `
    <div class="free-plan-badge">
      <div class="free-plan-name">POINTBUNNY POS</div>
      <div class="free-plan-slogan">The all-in-one POS<br>to run your shop</div>
      <div class="free-plan-cta">pointbunny.com</div>
    </div>` : `
    <p style="margin-top:2px;font-weight:bold;">Powered by Pointbunny POS</p>`}
  </div>`;

    return { css, page, contentWidth, body };
  }

  _generateReceiptHTML(sale, opts) {
    const { css, page, contentWidth, body } = this._receiptParts(sale, opts);
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Receipt</title>
<style>${css("body")}
  @media print { body { width:${contentWidth}; } ${page} }
</style>
<script>
  window.addEventListener('afterprint', function() {
    if (window.opener) window.opener.postMessage('pointbunny-afterprint', window.location.origin);
    setTimeout(function() { window.close(); }, 150);
  });
</script>
</head>
<body>${body}
</body>
</html>`;
  }

  // Same HTML that prints - used for the pre-commit preview in an iframe so the
  // preview matches the printout exactly. (Invoice/ticket numbers are assigned on
  // record, so a pre-record preview sale simply omits those lines.)
  previewMarkup(sale, opts) {
    return this._generateReceiptHTML(sale, opts);
  }

  // Android Chrome / Samsung Internet / Firefox-mobile block or mishandle the
  // window.open print popup - especially when it is opened after an await (the
  // sale is recorded before printing), which takes it outside the user-gesture
  // stack. iOS Safari and desktop handle the popup reliably, so only those stay
  // on the popup path; every other mobile browser prints via a hidden iframe,
  // which needs no popup permission. Any platform also falls back to the iframe
  // if the popup turns out to be blocked.
  _isIOS() {
    const ua = navigator.userAgent || "";
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  }

  _preferIframePrint() {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent || "");
    return isMobile && !this._isIOS();
  }

  print(sale, opts) {
    // iOS/iPadOS: neither child-window route is reliable. The popup is blocked
    // when it opens after the sale's await, and the iframe fallback's
    // contentWindow.print() prints the PARENT page on WebKit - which is how an
    // iPad ended up printing the whole Checkout screen (2026-10-04). Printing
    // inside the app page with print-only CSS has no child window to lose.
    if (this._isIOS()) return this._printInPage(this._receiptParts(sale, opts));

    const html = this._generateReceiptHTML(sale, opts);

    if (this._preferIframePrint()) return this._printViaIframe(html);

    let win = null;
    try {
      win = window.open(
        "",
        "_blank",
        "width=380,height=600,scrollbars=no,toolbar=no,menubar=no,location=no",
      );
    } catch {
      win = null;
    }
    // Popup blocked (common when opened outside a user gesture) - fall back to
    // the same-document iframe so the receipt still prints.
    if (!win) return this._printViaIframe(html);

    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 300);

    return new Promise((resolve) => {
      const cleanup = () => {
        window.removeEventListener("message", handler);
        clearTimeout(fallback);
        // Close the receipt window from this side too (the popup self-closes on
        // afterprint, but that doesn't fire on every browser / when the print
        // dialog is dismissed) and bring focus back so the confirm prompt is on
        // top instead of sitting behind the receipt window.
        try { if (win && !win.closed) win.close(); } catch { /* cross-origin guard */ }
        try { window.focus(); } catch { /* noop */ }
        resolve();
      };
      const handler = (e) => { if (e.data === "pointbunny-afterprint") cleanup(); };
      const fallback = setTimeout(cleanup, 60_000);
      window.addEventListener("message", handler);
    });
  }

  // Print by writing the receipt into a hidden, same-origin iframe and calling
  // its own print(). No popup window is opened, so popup blockers and the
  // after-await gesture restriction never apply - this is the reliable path on
  // Android Chrome and other mobile browsers.
  _printViaIframe(html) {
    return new Promise((resolve) => {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.cssText =
        "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(fallback);
        // Remove on a delay so the print job is not torn down mid-render.
        setTimeout(() => { try { iframe.remove(); } catch { /* noop */ } }, 1000);
        try { window.focus(); } catch { /* noop */ }
        resolve();
      };
      const fallback = setTimeout(finish, 60_000);

      document.body.appendChild(iframe);
      iframe.onload = () => {
        const cw = iframe.contentWindow;
        try {
          cw.addEventListener("afterprint", finish, { once: true });
          cw.focus();
          cw.print();
        } catch {
          finish();
        }
      };
      // srcdoc fires a reliable load event across browsers (more so than a
      // document.write into a blank iframe).
      iframe.srcdoc = html;
    });
  }

  // Prints the receipt as part of the app's own document: a hidden #pbPrintRoot
  // holds the receipt, and print-only CSS hides every other top-level element.
  // The app never shows on paper, and no popup or iframe is involved.
  _printInPage({ css, page, body }) {
    document.getElementById("pbPrintRoot")?.remove();
    document.getElementById("pbPrintStyle")?.remove();

    const style = document.createElement("style");
    style.id = "pbPrintStyle";
    style.textContent = `
@media screen { #pbPrintRoot { display:none !important; } }
@media print {
  html, body { height:auto !important; min-height:0 !important; overflow:visible !important; background:#fff !important; }
  body::before, body::after { display:none !important; }
  body > *:not(#pbPrintRoot) { display:none !important; }
  #pbPrintRoot { display:block !important; }
  ${css("#pbPrintRoot")}
}
${page}`;
    const root = document.createElement("div");
    root.id = "pbPrintRoot";
    root.setAttribute("aria-hidden", "true");
    root.innerHTML = body;
    document.head.appendChild(style);
    document.body.appendChild(root);

    // The receipt is deliberately NOT removed on afterprint: the iOS print sheet
    // re-renders its preview when the paper size changes, and WebKit's
    // afterprint timing is not a reliable "sheet closed" signal - removing it
    // early would print the app again. It is invisible on screen and gets
    // replaced at the top of the next _printInPage call.
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(fallback);
        window.removeEventListener("afterprint", finish);
        resolve();
      };
      const fallback = setTimeout(finish, 60_000);
      window.addEventListener("afterprint", finish);
      try {
        window.print();
      } catch {
        finish();
      }
    });
  }

}

export default new ReceiptView();
